import { useEffect, useRef } from 'react';
import { type BookingChanged, watchBooking } from '../services/liveBookings';

/**
 * Be told the moment one of these bookings changes.
 *
 * A thin wrapper over `services/liveBookings` - the socket, its authentication
 * and its reconnects all live there, shared between every caller. This exists
 * so a screen can express "while I am mounted, watch these" without owning any
 * of that.
 *
 * ---------------------------------------------------------------------------
 * THE CALLBACK IS HELD IN A REF, AND THAT IS NOT A STYLE CHOICE
 * ---------------------------------------------------------------------------
 * Callers hand in an arrow built fresh on every render. As a dependency it
 * would tear down and rebuild the subscription on every draw - and a rebuilt
 * subscription re-joins the room, which resyncs, which refetches the booking,
 * which re-renders. That is a loop, and it is a loop made of network requests.
 *
 * So the effect depends on the ids and on `enabled` only, and the callback is
 * read through a ref that each render updates.
 */
export function useBookingEvents(
  bookingIds: string[],
  /** False when signed out, or when nothing on screen is live. */
  enabled: boolean,
  onChanged: (bookingId: string, message: BookingChanged) => void,
): void {
  const handler = useRef(onChanged);
  handler.current = onChanged;

  /* The ids as one string, so an array rebuilt with the same contents - which
     is what a `.filter().map()` in a parent hands back every render - does not
     read as a change. Sorted and de-duplicated so the order they arrive in
     cannot matter either. */
  const key = [...new Set(bookingIds.filter(Boolean))].sort().join(',');

  useEffect(() => {
    if (!enabled || !key) return;

    const unsubscribes = key
      .split(',')
      .map(id =>
        watchBooking(id, message => handler.current(id, message)),
      );

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [enabled, key]);
}

export default useBookingEvents;
